/**
 * @file Decision logic for the `/patient/form` route guard (`PatientFormRoute` in `App.tsx`).
 * @module utils/patientFormRouteGuard
 *
 * Extracted as a pure function on purpose: `src/App.test.tsx` is excluded from
 * `jest.config.js` (it needs Vite's `import.meta.env`), so the guard cannot be tested where
 * it lives. `App.tsx` keeps only the cookie/URL reads; every branch is decided here.
 *
 * ⚠️ THIS GUARD USED TO REDIRECT EVERY SIGNED-IN USER, AND THAT WAS THE REPORTED BUG.
 * `/patient/form` is same-origin with the clinical app, so a practitioner opening a patient's
 * form from the Forms tab carries the session cookie into the new tab, was bounced to
 * `/patient`, and was bounced again by `PatientRoute` to `/login`. The rule that was actually
 * wanted is narrower: a signed-in PATIENT should use their dashboard rather than a raw link.
 * Everyone else — practitioner, admin, logged-out patient — gets the form.
 */

export type PatientFormRouteDecision = "allow" | "redirect-patient-dashboard";

export interface PatientFormRouteInput {
	/** The `?t=` access token off the URL, if the visitor arrived on a tokenised link. */
	token?: string | null;
	/** identity says a session exists — *some* session, not which kind. */
	signedIn?: boolean;
	/** The session's audience is patient, the same test the `/patient` guard applies. */
	patientSession?: boolean;
}

/**
 * A token-bearing link always wins: it is the capability the emailed/QR link carries, it is
 * what the backend authenticates on, and it is deliberately usable by a visitor with no
 * account at all. A practitioner testing a patient's link must not be silently redirected
 * away from the exact URL the patient was sent.
 */
export const resolvePatientFormRoute = ({
	token,
	signedIn,
	patientSession,
}: PatientFormRouteInput): PatientFormRouteDecision => {
	if (token) return "allow";

	if (signedIn && patientSession) return "redirect-patient-dashboard";

	return "allow";
};
